import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import type { CredentialProgress } from '../../../../lib/api/credential-progress.api';
import { programEnrollmentApi } from '../../../../lib/api/program-enrollment.api';
import { db } from '../../../../lib/firebase';
import { errorText } from '../../../../lib/error-text';
import { formatDate } from '../../../../lib/i18n-format';
import { openGeneratedPdf } from '../../../../lib/open-generated-pdf';
import { renderWithoutHyphenation } from '../../../../lib/pdf-hyphenation';
import { useTenantStore } from '../../../../stores/tenant.store';
import type { RecordAudience } from './academic-record.model';
import { buildRecordPdfData, type Tr } from './academic-record.pdf';

// ─── El transcript no oficial (PDF) ─────────────────────────────────────────
// Una sola implementación para la pestaña Expediente y el inicio del portal.
// Se arma con los MISMOS datos que la pantalla: el papel y el monitor no pueden
// discrepar. La pestaña se abre dentro del clic (openGeneratedPdf); si se
// abriera después de los await, el navegador la bloquearía en silencio.
// `progress` ya viene pasado por `progressFor(…, audience)`.

export function useRecordPdf(progress: CredentialProgress, audience: RecordAudience) {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // «Programa: …» en el papel: las matrículas del alumno (la misma consulta que
  // la pestaña de programas) con el nombre de cada programa.
  const studentProgramNames = async (): Promise<string[]> => {
    const [enrollments, programsSnap] = await Promise.all([
      queryClient.fetchQuery({
        queryKey: ['person-program-enrollments', currentTenantId, progress.personId],
        queryFn: () =>
          programEnrollmentApi.listStudentProgramEnrollments({
            tenantId: currentTenantId!,
            studentId: progress.personId,
          }),
        staleTime: 60_000,
      }),
      getDocs(collection(db, `tenants/${currentTenantId}/programs`)),
    ]);
    const names = new Map(
      programsSnap.docs.map((d) => [d.id, (d.data()['name'] as string | undefined) ?? '']),
    );
    return enrollments.items.map((e) => names.get(e.programId) ?? '');
  };

  const download = async () => {
    setError(null);
    const filename = `transcript-${progress.studentCode ?? 'record'}`;
    try {
      await openGeneratedPdf(async () => {
        const [{ pdf }, { AcademicRecordPdfDocument }, tenantSnap, programs] = await Promise.all([
          import('@react-pdf/renderer'),
          import('./AcademicRecordPdfDocument'),
          getDoc(doc(db, `tenants/${currentTenantId}`)),
          studentProgramNames(),
        ]);
        const tenant = (tenantSnap.data() ?? {}) as Record<string, unknown>;
        const data = buildRecordPdfData({
          progress,
          audience,
          tenant: {
            name: (tenant['name'] as string | undefined) ?? '',
            config: (tenant['config'] ?? {}) as Record<string, unknown>,
          },
          programs,
          issuedOn: formatDate(new Date(), { dateStyle: 'long' }),
          formatDay: (ymd) => formatDate(`${ymd}T12:00:00`, { dateStyle: 'long' }),
          tr: t as unknown as Tr,
        });
        return renderWithoutHyphenation(() =>
          pdf(<AcademicRecordPdfDocument data={data} />).toBlob(),
        );
      }, filename);
    } catch (e) {
      setError(errorText(e, t('academicRecord.pdf.error')));
    }
  };

  return { download, error };
}
